import { useEffect, useState } from "react";
import { api } from "@/lib/soul";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";

const MIRROR_IMG =
  "https://images.unsplash.com/photo-1595311098506-3d06eda55262?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjYWxtJTIwcXVpZXQlMjBuYXR1cmUlMjBtaXJyb3J8ZW58MHx8fHwxNzg1MzQ4OTY2fDA&ixlib=rb-4.1.0&q=85";

export default function Mirror() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/mirror/questions").then((r) => setQuestions(r.data.questions));
  }, []);

  const choose = (qid, option) => {
    setAnswers((a) => ({ ...a, [qid]: option }));
  };

  const reveal = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Answer each mirror to see your reflection.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/mirror/reflect", { answers });
      setResult(r.data);
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
    } catch {
      toast.error("Could not reflect. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main data-testid="mirror-page" className="max-w-7xl mx-auto px-6 py-16">
      <section className="relative rounded-3xl overflow-hidden mb-14">
        <img src={MIRROR_IMG} alt="Still water reflection" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#110D14] via-[#110D14]/80 to-[#110D14]/40" />
        <div className="relative z-10 p-10 sm:p-16 max-w-2xl">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">Relationship Mirror</p>
          <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
            Everyone you meet <span className="italic-serif text-[#D4AF37]">is a mirror.</span>
          </h1>
          <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
            There is a reason we attract who we attract. Answer honestly — the
            reflection you receive is a doorway, not a verdict.
          </p>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6" data-testid="mirror-questions">
        {questions.map((q, i) => (
          <div key={q.id} className="glass rounded-2xl p-7" style={{ gridColumn: i === questions.length - 1 && questions.length % 2 === 1 ? "1 / -1" : "auto" }}>
            <p className="italic-serif text-2xl text-[#F5F1E7] leading-snug mb-5">{q.question}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map((opt) => (
                <button
                  key={opt}
                  data-testid={`mirror-${q.id}-${opt}`}
                  onClick={() => choose(q.id, opt)}
                  className={`text-sm px-4 py-2 rounded-full border transition-colors duration-300 ${
                    answers[q.id] === opt
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-white/10 text-[#A8A29E] hover:border-[#D4AF37]/40"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex gap-4">
        <button
          data-testid="reveal-mirror-btn"
          onClick={reveal}
          disabled={loading}
          className="rounded-full bg-[#D4AF37] text-[#110D14] px-8 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 gold-glow disabled:opacity-50"
        >
          {loading ? "Reflecting..." : "Reveal my reflection"}
        </button>
        {result && (
          <button
            data-testid="reset-mirror-btn"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
          >
            <RotateCcw size={18} /> Start over
          </button>
        )}
      </div>

      {result && (
        <section data-testid="mirror-result" className="mt-14 space-y-5 fade-up">
          <h2 className="serif text-4xl text-[#F5F1E7]">Your reflection</h2>
          {result.insights.map((ins, i) => (
            <div key={i} className="glass rounded-2xl p-7 border-l-2" style={{ borderLeftColor: "#D4AF37" }}>
              <p className="text-sm text-[#A8A29E]">{ins.question}</p>
              <p className="text-[#D4AF37] mt-1 mb-3 tracking-wide">{ins.answer}</p>
              <p className="text-[#F5F1E7] text-lg leading-relaxed italic-serif">{ins.insight}</p>
            </div>
          ))}
          <div className="glass rounded-2xl p-8 border-l-2" style={{ borderLeftColor: "#C1272D" }}>
            <p className="text-[#F5F1E7] text-xl leading-relaxed italic-serif">{result.summary}</p>
          </div>
        </section>
      )}
    </main>
  );
}
